import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useOrgPermissions } from '@/hooks/useOrgPermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ContractVariables from '@/components/ContractVariables';

interface TipoProposta {
  id: string;
  nome: string;
}

interface ModeloProposta {
  id: string;
  nome_modelo: string;
  tipo_proposta_id: string;
  conteudo_html: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export default function ModeloPropostaForm() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission, refetch } = usePermissions();
  const { userOrgInfo } = useOrgPermissions();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const [tiposPropostas, setTiposPropostas] = useState<TipoProposta[]>([]);
  
  const [formData, setFormData] = useState({
    nome_modelo: '',
    tipo_proposta_id: '',
    conteudo_html: ''
  });
  const [quillRef, setQuillRef] = useState<any>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const canRead = hasPermission('recursos_humanos', 'modelos_proposta', 'read');
  const canWrite = hasPermission('recursos_humanos', 'modelos_proposta', 'write');

  const isEditMode = !!id;

  useEffect(() => {
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked) return;
    if (!canWrite) {
      navigate('/intranet');
      return;
    }
    fetchTiposPropostas();
    if (isEditMode && id) {
      fetchModelo();
    }
  }, [canWrite, permissionsChecked, id, isEditMode]);

  const fetchTiposPropostas = async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_proposta')
        .select('id, nome')
        .eq('status', 'ativo')
        .order('nome');

      if (error) throw error;

      setTiposPropostas(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar tipos de proposta",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const fetchModelo = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('modelo_proposta')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          nome_modelo: data.nome_modelo,
          tipo_proposta_id: data.tipo_proposta_id,
          conteudo_html: data.conteudo_html
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelo de proposta",
        description: error.message,
        variant: "destructive",
      });
      navigate('/intranet/modelo-proposta');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nome_modelo.trim() || !formData.tipo_proposta_id || !formData.conteudo_html.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);

      if (isEditMode && id) {
        const { error } = await supabase
          .from('modelo_proposta')
          .update({
            ...formData,
            updated_by: user?.id
          })
          .eq('id', id);

        if (error) throw error;

        toast({
          title: "Modelo de proposta atualizado com sucesso!",
        });
      } else {
        const { error } = await supabase
          .from('modelo_proposta')
          .insert([{
            ...formData,
            created_by: user?.id,
            org_id: userOrgInfo?.orgId
          }]);

        if (error) throw error;

        toast({
          title: "Modelo de proposta criado com sucesso!",
        });
      }

      navigate('/intranet/modelos-proposta');
    } catch (error: any) {
      toast({
        title: `Erro ao ${isEditMode ? 'atualizar' : 'criar'} modelo de proposta`,
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const text = e.dataTransfer.getData('text/plain');
    if (text && quillRef) {
      const quill = quillRef.getEditor();
      const range = quill.getSelection();
      const index = range ? range.index : quill.getLength();
      quill.insertText(index, text);
      quill.setSelection(index + text.length);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Configurar a referência do Quill
  const handleQuillRef = (ref: any) => {
    setQuillRef(ref);
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      [{ 'align': [] }],
      [{ 'color': [] }],
      [{ 'background': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'link', 'align', 'color', 'background'
  ];

  if (!permissionsChecked) {
    return (
      <div className="p-6">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!canWrite) {
    return (
      <div className="p-6">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">Acesso Negado</h2>
          <p className="text-muted-foreground">
            Você não tem permissão para {isEditMode ? 'editar' : 'criar'} modelos de proposta.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => navigate('/intranet/modelo-proposta')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditMode ? 'Editar Modelo de Proposta' : 'Novo Modelo de Proposta'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Altere os dados do modelo de proposta' : 'Preencha os dados para criar um novo modelo de proposta'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Informações Básicas</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="nome_modelo">Nome do Modelo *</Label>
                <Input
                  id="nome_modelo"
                  value={formData.nome_modelo}
                  onChange={(e) => setFormData({ ...formData, nome_modelo: e.target.value })}
                  placeholder="Digite o nome do modelo"
                />
              </div>
              
              <div>
                <Label htmlFor="tipo_proposta_id">Tipo de Proposta *</Label>
                <Select 
                  value={formData.tipo_proposta_id} 
                  onValueChange={(value) => setFormData({ ...formData, tipo_proposta_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um tipo de proposta" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposPropostas.map((tipo) => (
                      <SelectItem key={tipo.id} value={tipo.id}>
                        {tipo.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
          
          <ContractVariables />
        </div>

        <div className="lg:col-span-2">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Conteúdo da Proposta</h3>
            <div>
              <Label htmlFor="conteudo_html">Conteúdo do Modelo *</Label>
              <div 
                className={`mt-2 min-h-[400px] transition-all duration-200 ${isDragOver ? 'ring-2 ring-primary ring-opacity-50 bg-primary/5' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <ReactQuill
                  ref={handleQuillRef}
                  theme="snow"
                  value={formData.conteudo_html}
                  onChange={(value) => setFormData({ ...formData, conteudo_html: value })}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: '350px', marginBottom: '50px' }}
                  placeholder="Digite o conteúdo do modelo de proposta ou arraste variáveis aqui..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => navigate('/intranet/modelo-proposta')}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : (isEditMode ? 'Atualizar' : 'Criar')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}